import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api, { employeeAPI, organizationAPI, verticalAPI, settingsAPI, incrementAPI, departmentAPI } from '@/lib/api';
import { EpfBadge } from '@/components/EpfBadge';
import CancelledChequeUpload from '@/components/employees/CancelledChequeUpload';
import EmailInlineCell from '@/components/employees/EmailInlineCell';
import BulkEmailImportDialog from '@/components/employees/BulkEmailImportDialog';
import { copyText } from '@/lib/clipboard';
import EmployeeCountCard from '@/components/employees/EmployeeCountCard';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  Loader2,
  Search,
  Filter,
  TrendingUp,
  History,
  Download,
  AlertTriangle,
  XCircle,
  UserCheck,
  FileDown,
  PenTool,
  Eye,
  Mail,
  Upload,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

function fmtINR(n) {
  if (!n || n === 0) return '₹0';
  return '₹' + Math.round(n).toLocaleString('en-IN');
}

// Yearly = round(monthly) * 12 so column math is exact when read by user
function fmtYr(n) {
  if (!n || n === 0) return '₹0';
  return '₹' + (Math.round(n) * 12).toLocaleString('en-IN');
}

function SalaryStructurePreview({ gross, retention, annualBonus, epfEnabled }) {
  if (!gross || gross <= 0) return null;

  // Compute components (new rules: Basic 50% Gross, HRA 50% Basic, EPF ₹1,800 fixed)
  const basic = gross * 0.50;
  const hra = basic * 0.50;
  const empPF = epfEnabled ? 1800 : 0;
  const emprPF = epfEnabled ? 1800 : 0;
  const gratuityYearly = epfEnabled ? basic * 0.0481 * 12 : 0;
  // PLI = balancing remainder
  const pli = epfEnabled
    ? gross - basic - hra - retention - empPF
    : gross - basic - hra - retention;

  const ab = Math.round(annualBonus || 0);
  const grat = Math.round(gratuityYearly);

  // Component yearly = round(monthly) × 12
  const yrBasic = Math.round(basic) * 12;
  const yrHra = Math.round(hra) * 12;
  const yrRet = Math.round(retention) * 12;
  const yrPli = Math.round(pli > 0 ? pli : 0) * 12;
  const yrEmpPF = Math.round(empPF) * 12;
  const yrEmprPF = Math.round(emprPF) * 12;

  // Total Earnings / Deductions / Net / CTC — yearly aggregates include AB + Gratuity once a year
  const totalEarningsMonthly = gross;
  const totalEarningsYearly = Math.round(gross) * 12 + ab + grat;
  const totalDeductionsMonthly = empPF;
  const totalDeductionsYearly = yrEmpPF;
  const netMonthly = gross - empPF;
  const netYearly = totalEarningsYearly - totalDeductionsYearly;
  const ctcMonthly = gross + emprPF;
  const ctcYearly = ctcMonthly === 0 ? 0 : Math.round(ctcMonthly) * 12 + ab + grat;

  const fmt = (n) => '₹' + Math.round(n || 0).toLocaleString('en-IN');
  const fmtY = (n) => '₹' + Math.round(n || 0).toLocaleString('en-IN');

  // Earnings rows (left side)
  const earningRows = [
    { label: 'Basic', sub: '(50% of Gross)', m: basic, y: yrBasic },
    { label: 'HRA', sub: '(50% of Basic)', m: hra, y: yrHra },
    { label: 'Retention Bonus', m: retention, y: yrRet },
    { label: 'PLI', sub: '(remainder)', m: pli > 0 ? pli : 0, y: yrPli },
    { label: 'Annual Bonus', sub: '(yearly only)', m: null, y: ab, italic: true },
  ];
  if (epfEnabled) {
    earningRows.push({ label: 'Gratuity', sub: '(Basic × 4.81% × 12)', m: null, y: grat, italic: true });
  }

  // Deduction rows (right side)
  const deductionRows = epfEnabled
    ? [
        { label: 'Provident Fund (Employee)', sub: '(fixed ₹1,800)', m: empPF, y: yrEmpPF },
        { label: 'Income Tax (TDS)', m: null, y: null },
      ]
    : [{ label: 'Income Tax (TDS)', m: null, y: null }];

  // Pad shorter side with empty rows so the two columns align visually
  const maxRows = Math.max(earningRows.length, deductionRows.length);
  while (earningRows.length < maxRows) earningRows.push({ blank: true });
  while (deductionRows.length < maxRows) deductionRows.push({ blank: true });

  return (
    <div className="rounded-md border-2 border-slate-700 dark:border-slate-300 overflow-hidden bg-white dark:bg-slate-900" data-testid="salary-structure-preview">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white dark:bg-slate-900 border-b-2 border-slate-700 dark:border-slate-300">
        <span className="text-sm font-bold">Salary Break Up</span>
        {epfEnabled ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">EPF ENABLED</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-bold">NO EPF</span>
        )}
      </div>

      {/* Earnings / Deductions side-by-side */}
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-100 dark:bg-slate-800">
            <th colSpan={3} className="text-left px-3 py-2 font-bold border-r-2 border-b-2 border-slate-700 dark:border-slate-300" style={{ width: '50%' }}>EARNINGS</th>
            <th colSpan={3} className="text-left px-3 py-2 font-bold border-b-2 border-slate-700 dark:border-slate-300" style={{ width: '50%' }}>DEDUCTIONS</th>
          </tr>
          <tr className="bg-slate-50 dark:bg-slate-800/50 text-[11px] text-muted-foreground font-medium">
            <th className="text-left px-3 py-1.5 font-normal border-b border-slate-300">Component</th>
            <th className="text-right px-3 py-1.5 font-normal border-b border-slate-300 font-mono w-20">Monthly</th>
            <th className="text-right px-3 py-1.5 font-normal border-b border-slate-300 border-r-2 border-r-slate-700 dark:border-r-slate-300 font-mono w-20">Yearly</th>
            <th className="text-left px-3 py-1.5 font-normal border-b border-slate-300">Component</th>
            <th className="text-right px-3 py-1.5 font-normal border-b border-slate-300 font-mono w-20">Monthly</th>
            <th className="text-right px-3 py-1.5 font-normal border-b border-slate-300 font-mono w-20">Yearly</th>
          </tr>
        </thead>
        <tbody>
          {earningRows.map((er, idx) => {
            const dr = deductionRows[idx] || { blank: true };
            return (
              <tr key={idx} className="border-b border-slate-200 dark:border-slate-700">
                {/* Earnings cell */}
                {er.blank ? (
                  <>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 border-r-2 border-r-slate-700 dark:border-r-slate-300"></td>
                  </>
                ) : (
                  <>
                    <td className={`px-3 py-2 ${er.italic ? 'italic text-muted-foreground' : ''}`}>
                      {er.label}
                      {er.sub && <span className="ml-1 text-[10px] text-muted-foreground">{er.sub}</span>}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${er.italic ? 'text-muted-foreground' : ''}`}>
                      {er.m === null ? '—' : fmt(er.m)}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono border-r-2 border-r-slate-700 dark:border-r-slate-300 ${er.italic ? 'text-muted-foreground' : ''}`}>
                      {er.y === null ? '—' : (er.y === 0 && er.italic ? '—' : fmtY(er.y))}
                    </td>
                  </>
                )}
                {/* Deduction cell */}
                {dr.blank ? (
                  <>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2"></td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2">{dr.label}</td>
                    <td className="px-3 py-2 text-right font-mono">{dr.m === null ? '—' : fmt(dr.m)}</td>
                    <td className="px-3 py-2 text-right font-mono">{dr.y === null ? '—' : fmtY(dr.y)}</td>
                  </>
                )}
              </tr>
            );
          })}
          {/* Totals row */}
          <tr className="bg-slate-100 dark:bg-slate-800 font-bold border-y-2 border-slate-700 dark:border-slate-300">
            <td className="px-3 py-2.5">Total Earnings</td>
            <td className="px-3 py-2.5 text-right font-mono">{fmt(totalEarningsMonthly)}</td>
            <td className="px-3 py-2.5 text-right font-mono border-r-2 border-r-slate-700 dark:border-r-slate-300">{fmtY(totalEarningsYearly)}</td>
            <td className="px-3 py-2.5">Total Deductions</td>
            <td className="px-3 py-2.5 text-right font-mono">{fmt(totalDeductionsMonthly)}</td>
            <td className="px-3 py-2.5 text-right font-mono">{fmtY(totalDeductionsYearly)}</td>
          </tr>
        </tbody>
      </table>

      {/* Employer PF strip (EPF orgs only) */}
      {epfEnabled && (
        <div className="px-4 py-2 bg-blue-50 dark:bg-blue-950/30 text-[11px] text-blue-700 dark:text-blue-300 italic flex justify-between items-center border-b border-slate-300">
          <span>
            + Employer PF Contribution <span className="text-[10px]">(fixed ₹1,800 · not deducted from salary; included in CTC)</span>
          </span>
          <span className="font-mono font-semibold not-italic">
            Monthly {fmt(emprPF)} · Yearly {fmtY(yrEmprPF)}
          </span>
        </div>
      )}

      {/* Net Pay + CTC footer */}
      <div className="grid grid-cols-2 bg-emerald-700 text-white">
        <div className="px-4 py-3 border-r-2 border-emerald-500">
          <div className="text-[11px] uppercase tracking-wide opacity-80">Net Final Payable</div>
          <div className="text-base font-bold font-mono">
            {fmt(netMonthly)} <span className="text-[11px] opacity-80 font-normal">/ mo</span>
          </div>
          <div className="text-[11px] font-mono opacity-90">
            {fmtY(netYearly)} <span className="opacity-70 font-normal">/ yr</span>
          </div>
        </div>
        {epfEnabled ? (
          <div className="px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide opacity-80">Cost to Company</div>
            <div className="text-base font-bold font-mono">
              {fmt(ctcMonthly)} <span className="text-[11px] opacity-80 font-normal">/ mo</span>
            </div>
            <div className="text-[11px] font-mono opacity-90">
              {fmtY(ctcYearly)} <span className="opacity-70 font-normal">/ yr</span>
            </div>
          </div>
        ) : (
          <div className="px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide opacity-80">Total Earnings (Gross)</div>
            <div className="text-base font-bold font-mono">
              {fmt(totalEarningsMonthly)} <span className="text-[11px] opacity-80 font-normal">/ mo</span>
            </div>
            <div className="text-[11px] font-mono opacity-90">
              {fmtY(totalEarningsYearly)} <span className="opacity-70 font-normal">/ yr</span>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-2 flex items-start gap-1.5 text-[10px] text-muted-foreground italic bg-white dark:bg-slate-900">
        <span className="text-emerald-600 font-bold">✓</span>
        <span>Yearly = Monthly × 12 for every component row. Total Earnings (Yearly) &amp; CTC include Annual Bonus + Gratuity rolled in once a year.</span>
      </div>
    </div>
  );
}

function DocTabContent({ employee, formData, organizations }) {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [signerEmployee, setSignerEmployee] = useState('');
  const [signerHR, setSignerHR] = useState('');
  const [signerMgmt, setSignerMgmt] = useState('');
  const [sendingSign, setSendingSign] = useState(false);
  const [signRequests, setSignRequests] = useState([]);

  const org = organizations.find(o => o.id === formData.organization_id);
  const isEpf = org ? (org.epf_enabled || false) : false;
  const gross = parseFloat(formData.gross_salary) || 0;
  const retention = parseFloat(formData.retention_amount) || 0;
  const annualBonus = parseFloat(formData.annual_bonus) || 0;
  const empName = formData.name || '';
  const empCode = formData.employee_id || '';
  const orgName = org ? org.name : '';
  const verticalName = formData.vertical_id || '';

  useEffect(() => {
    if (employee) {
      api.get('/legal/sign-requests').then(res => {
        const filtered = res.data.filter(r => r.employee_id_code === empCode || r.employee_name === empName);
        setSignRequests(filtered);
      }).catch(() => {});
    }
  }, [employee, empCode, empName]);

  const handlePreviewDoc = () => {
    if (gross <= 0) { toast.error('Set Gross Salary first'); return; }
    const dateStr = effectiveDate ? new Date(effectiveDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '[Effective Date]';
    // New rules: Basic = Gross × 50%, HRA = Basic × 50%, EPF fixed ₹1,800
    const basic = gross * 0.50;
    const hra = basic * 0.50;
    const empPF = isEpf ? 1800 : 0;
    const emprPF = isEpf ? 1800 : 0;
    // Gratuity is yearly-only — Basic × 4.81% × 12
    const gratuityYearly = isEpf ? basic * 0.0481 * 12 : 0;
    // PLI = balancing remainder
    const pli = isEpf ? gross - basic - hra - retention - empPF : gross - basic - hra - retention;
    const subTotal = gross;
    // CTC Monthly = Sub Total + PF Employer
    const ctcFinal = isEpf ? subTotal + emprPF : gross;
    const netPayable = isEpf ? gross - empPF : gross;
    const f = (n) => n ? Math.round(n).toLocaleString('en-IN') : '0';
    const fy = (n) => n ? (Math.round(n) * 12).toLocaleString('en-IN') : '0';
    const ab = annualBonus ? Math.round(annualBonus) : 0;
    const grat = gratuityYearly ? Math.round(gratuityYearly) : 0;
    // Aggregate rows include Annual Bonus + Gratuity once a year
    const fyAgg = (m) => (Math.round(m) * 12 + ab + grat).toLocaleString('en-IN');

    let tableRows = '';
    // Earnings rows (left column)
    const earningsRows = [
      { label: 'Basic <span style="font-size:10px;color:#94a3b8">(50% of Gross)</span>', m: basic, y: Math.round(basic) * 12 },
      { label: 'HRA <span style="font-size:10px;color:#94a3b8">(50% of Basic)</span>', m: hra, y: Math.round(hra) * 12 },
      { label: 'Retention Bonus', m: retention, y: Math.round(retention) * 12 },
      { label: 'PLI <span style="font-size:10px;color:#94a3b8">(remainder)</span>', m: pli > 0 ? pli : 0, y: Math.round(pli > 0 ? pli : 0) * 12 },
      { label: 'Annual Bonus <span style="font-size:10px;color:#777">(yearly only)</span>', m: null, y: ab > 0 ? ab : null, italic: true },
    ];
    if (isEpf) {
      earningsRows.push({ label: 'Gratuity <span style="font-size:10px;color:#777">(Basic × 4.81% × 12)</span>', m: null, y: grat, italic: true });
    }
    // Deduction rows (right column)
    const deductionsRows = isEpf
      ? [
          { label: 'Provident Fund (Employee) <span style="font-size:10px;color:#94a3b8">(fixed ₹1,800)</span>', m: empPF, y: Math.round(empPF) * 12 },
          { label: 'Income Tax (TDS)', m: null, y: null },
        ]
      : [{ label: 'Income Tax (TDS)', m: null, y: null }];
    // Pad shorter side
    while (earningsRows.length < deductionsRows.length) earningsRows.push({ blank: true });
    while (deductionsRows.length < earningsRows.length) deductionsRows.push({ blank: true });
    const maxRows = earningsRows.length;
    for (let i = 0; i < maxRows; i++) {
      const er = earningsRows[i] || { blank: true };
      const dr = deductionsRows[i] || { blank: true };
      const eCell = er.blank
        ? `<td></td><td class="r"></td><td class="r br">&nbsp;</td>`
        : `<td${er.italic ? ' style="font-style:italic;color:#777"' : ''}>${er.label}</td>` +
          `<td class="r"${er.italic ? ' style="color:#777"' : ''}>${er.m === null ? '—' : f(er.m)}</td>` +
          `<td class="r br"${er.italic ? ' style="color:#777"' : ''}>${er.y === null ? '—' : (er.y === 0 ? '—' : f(er.y))}</td>`;
      const dCell = dr.blank
        ? `<td></td><td class="r"></td><td class="r"></td>`
        : `<td>${dr.label}</td>` +
          `<td class="r">${dr.m === null ? '—' : f(dr.m)}</td>` +
          `<td class="r">${dr.y === null ? '—' : f(dr.y)}</td>`;
      tableRows += `<tr>${eCell}${dCell}</tr>`;
    }
    // Totals row
    const totalEarnY = Math.round(subTotal) * 12 + ab + grat;
    const totalDedY = Math.round(empPF) * 12;
    tableRows += `<tr class="totals"><td><b>Total Earnings</b></td><td class="r"><b>${f(subTotal)}</b></td><td class="r br"><b>${f(totalEarnY)}</b></td><td><b>Total Deductions</b></td><td class="r"><b>${f(empPF)}</b></td><td class="r"><b>${f(totalDedY)}</b></td></tr>`;
    // Footer with Net + CTC
    const netY = totalEarnY - totalDedY;
    const ctcY = Math.round(ctcFinal) * 12 + ab + grat;
    const employerStrip = isEpf
      ? `<tr class="employer"><td colspan="6">+ Employer PF Contribution <span style="font-size:10px">(fixed ₹1,800 · not deducted from salary; included in CTC)</span> &nbsp; — &nbsp; Monthly ₹${f(emprPF)} · Yearly ₹${f(Math.round(emprPF) * 12)}</td></tr>`
      : '';
    tableRows += employerStrip;
    const rightFooterLabel = isEpf ? 'Cost to Company' : 'Total Earnings (Gross)';
    const rightFooterM = isEpf ? ctcFinal : subTotal;
    const rightFooterY = isEpf ? ctcY : totalEarnY;
    tableRows += `<tr class="netpay">
      <td colspan="3" class="br"><div class="lbl">NET FINAL PAYABLE</div><div class="amt">₹${f(netPayable)} <span class="sm">/ mo</span></div><div class="sm2">₹${f(netY)} / yr</div></td>
      <td colspan="3"><div class="lbl">${rightFooterLabel.toUpperCase()}</div><div class="amt">₹${f(rightFooterM)} <span class="sm">/ mo</span></div><div class="sm2">₹${f(rightFooterY)} / yr</div></td>
    </tr>`;

    const annexureBContent = isEpf ? `
      <p style="margin-top:14px;font-size:12px">Point No. 2 is Subject to Travel and Company Policy.</p>
      <p style="font-size:12px">Point No. 3 Retention Bonus will be payable half yearly subject to organization and Individual performance. Any issues or complaints reported against you or you leave the organization not as per your appointment letter, the bonus shall be forfeited without any giving explanation.</p>
      <p style="font-size:12px">Point No. 4 Annual Bonus is Subject to date of joining and net earnings and Company Policy. The Bonus will be paid on a pro rata basis after the end of fiscal year. The amount mentioned is the maximum earning in the first year of joining. Bonus is payable as per performance and profitability of the overall company also in case any issues or complaints reported against you the bonus shall be forfeited without any giving explanation.</p>
      <p style="font-size:12px">Point No. 5 Performance Linked Incentive is based on certain parameters including sales and counseling of students and guiding them for better opportunities while creating business for the Company. It is a form of incentive payable on a condition where you need to maintain good relationship with your employees with proper management and coordination regarding the work given to you in case any issues or complaints reported against you the PLI shall be forfeited without any giving explanation. If everything is in favor this incentive shall be paid with your monthly salary.</p>
      <p style="font-size:12px">Point No. 6 Gratuity is applicable to be paid on completion of 5 years with the organization as per the Gratuity Act. The amount mentioned here is for one year as per your salary structure.</p>
      <p style="font-size:12px"><b>Note:</b> As per the provisions of the Employees' Provident Funds and Miscellaneous Provisions Act, 1952, the company is now covered under PF regulations. Accordingly, PF contributions will be applicable, with 12% of the basic salary contributed by the employee and 12% contributed by the employer. The employee's contribution will be deducted from the salary, while the employer's contribution will form part of the overall salary structure, including special allowances, effective from the date of PF applicability.</p>
      <p style="font-size:12px">Salary is private and confidential and should not be shared with anyone in the company. Any such act may lead to disciplinary action.</p>
      <p style="font-size:12px">TDS will be deducted as per the Income Tax Act from your salary (if applicable).</p>
    ` : `
      <p style="margin-top:14px;font-size:12px">Point No. 2 is Subject to Travel and Company Policy.</p>
      <p style="font-size:12px">Point No. 3 Retention Bonus will be payable half yearly subject to organization and Individual performance. Any issues or complaints reported against you or you leave the organization not as per your appointment letter, the bonus shall be forfeited without any giving explanation.</p>
      <p style="font-size:12px">Point No. 4 Annual Bonus is Subject to date of joining and net earnings and Company Policy. The Bonus will be paid on a pro rata basis after the end of fiscal year.</p>
      <p style="font-size:12px">Point No. 5 Performance Linked Incentive is based on certain parameters including sales and counseling of students and guiding them for better opportunities while creating business for the Company.</p>
      <p style="font-size:12px">Salary is private and confidential and should not be shared with anyone in the company. Any such act may lead to disciplinary action.</p>
      <p style="font-size:12px">TDS will be deducted as per the Income Tax Act from your salary (if applicable).</p>
    `;

    const html = `<!DOCTYPE html><html><head><title>Preview - ${empName}</title><style>
      body{font-family:Calibri,Arial,sans-serif;max-width:880px;margin:40px auto;padding:0 30px;color:#222;line-height:1.7;font-size:14px}
      h2{text-align:center;margin:30px 0 10px}
      table.payslip{width:100%;border-collapse:collapse;font-size:12.5px;margin-top:10px;border:1.5px solid #334155}
      table.payslip th,table.payslip td{padding:7px 10px;border-right:1px solid #cbd5e1;border-bottom:1px solid #e5e7eb}
      table.payslip th.br,table.payslip td.br{border-right:1.5px solid #334155}
      table.payslip thead .grouphead th{background:#f1f5f9;font-weight:bold;text-align:left;border-bottom:1.5px solid #334155;padding:8px 12px;font-size:13px}
      table.payslip thead .subhead th{background:#f8fafc;color:#64748b;font-weight:500;font-size:11px;border-bottom:1.5px solid #334155}
      table.payslip .r{text-align:right;font-family:'Courier New',monospace}
      table.payslip tr.totals td{background:#f1f5f9;font-weight:bold;border-top:1.5px solid #334155;border-bottom:1.5px solid #334155;font-size:13px}
      table.payslip tr.employer td{background:#eff6ff;color:#1e40af;font-size:11px;font-style:italic;border-right:none;padding:8px 14px}
      table.payslip tr.netpay td{background:#065f46;color:white;padding:14px 16px;border:none}
      table.payslip tr.netpay .lbl{font-size:10px;text-transform:uppercase;opacity:.85;letter-spacing:.6px}
      table.payslip tr.netpay .amt{font-size:15px;font-weight:bold;font-family:'Courier New',monospace;margin-top:2px}
      table.payslip tr.netpay .amt .sm{font-size:11px;opacity:.8;font-weight:normal}
      table.payslip tr.netpay .sm2{font-size:11px;font-family:'Courier New',monospace;opacity:.85;margin-top:1px}
      .page-break{page-break-before:always;margin-top:60px;border-top:2px dashed #334155;padding-top:30px}
      @media print{.page-break{page-break-before:always;margin-top:0;border:0;padding-top:0} body{margin:20px}}
    </style></head><body>
    <p style="text-align:right;font-size:12px">${dateStr}</p>
    <p><b>${empName}</b></p>
    <p>Dear ${empName},</p>
    <p>We are pleased with your performance with us since joining as part of the ${orgName} Team. We are happy to have you in our growing family.</p>
    <p>We would like to inform you that we are providing you an increment in your current salary payout as per your exceptional performance along with the latest government policy/regulatory updates. The company has revised the salary structure applicable to employees to ensure compliance with current statutory requirements.</p>
    <p>Your revised salary will be effective from <b>${dateStr}</b> onwards. Your all inclusive salary structure (Employee's cost to the company (CTC) will be paid in accordance with the Company's payroll procedures as detailed out in annexure "A".</p>
    <p>All the other terms and conditions of your employment remain unaltered. We look forward to your continued dedicated performance.</p>
    <p>This letter represents the full understanding between you and company and shall supersede all previous oral or written agreements regarding the above subject matter herein.</p>
    <p>Please intimate your acceptance by signing this letter electronically copy to the Human Resources department.</p>
    <p>We wish you a long and successful career with ${orgName}.</p>
    <p style="margin-top:40px"><b>Nayan Verma</b><br/>Director<br/>${orgName}</p>
    <p style="margin-top:40px"><b>ACCEPTANCE</b></p>
    <p>I, ${empName}, accept all the above terms and conditions of Employment.</p>
    <p>Signature of Employee</p>

    <div class="page-break">
    <h2>Annexure "A"</h2>
    <p style="text-align:center"><b>Salary Structure</b></p>
    <table class="payslip">
      <thead>
        <tr class="grouphead"><th colspan="3" class="br">EARNINGS</th><th colspan="3">DEDUCTIONS</th></tr>
        <tr class="subhead">
          <th style="text-align:left">Component</th><th class="r">Monthly</th><th class="r br">Yearly</th>
          <th style="text-align:left">Component</th><th class="r">Monthly</th><th class="r">Yearly</th>
        </tr>
      </thead>
      <tbody>${tableRows}</tbody>
    </table>
    <p style="margin-top:10px;font-size:11px;color:#5B2C8D;font-style:italic"><b>✓ Note:</b> Yearly = Monthly × 12 for every component row. Total Earnings (Yearly) &amp; CTC include Annual Bonus + Gratuity rolled in once a year.</p>
    </div>

    <div class="page-break">
    <h2>Annexure "B"</h2>
    <p style="text-align:center"><b>DEFINITIONS AND INTERPRETATION</b></p>
    ${annexureBContent}
    <p style="margin-top:50px"><b>ACCEPTANCE</b></p>
    <p>I, ${empName}, accept all the above terms and conditions of Employment.</p>
    <p style="margin-top:30px">Signature of Employee -</p>
    </div>
    </body></html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const handleDownloadDocx = async () => {
    if (gross <= 0) { toast.error('Set Gross Salary first'); return; }
    setGenerating(true);
    try {
      const response = await api.post('/legal/salary-slip/docx', {
        in_hand_salary: gross,
        retention_bonus: retention,
        annual_bonus: annualBonus,
        epf_enabled: isEpf,
        employee_name: empName,
        employee_id_code: empCode,
        organization_name: orgName,
        designation: formData.vertical_id || '',
        effective_date: effectiveDate || null,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Revision_${empName.replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Document downloaded');
    } catch (e) {
      toast.error('Failed to generate document');
    } finally { setGenerating(false); }
  };

  const handleSendForSign = async () => {
    if (!signerEmployee || !signerHR || !signerMgmt) { toast.error('Fill all signer emails'); return; }
    if (gross <= 0) { toast.error('Set Gross Salary first'); return; }
    setSendingSign(true);
    try {
      await api.post('/legal/sign-requests', {
        employee_name: empName,
        employee_id_code: empCode,
        organization_name: orgName,
        designation: formData.vertical_id || '',
        in_hand_salary: gross,
        retention_bonus: retention,
        effective_date: effectiveDate,
        signer_employee: signerEmployee,
        signer_hr: signerHR,
        signer_management: signerMgmt,
      });
      toast.success('Sent for signature via Zoho Sign');
      setSignerEmployee('');
      setSignerHR('');
      setSignerMgmt('');
      // Reload requests
      const res = await api.get('/legal/sign-requests');
      setSignRequests(res.data.filter(r => r.employee_id_code === empCode || r.employee_name === empName));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSendingSign(false); }
  };

  if (gross <= 0) {
    return <p className="text-sm text-muted-foreground p-4">Set Gross Salary in the Salary & Bonus tab first to generate documents.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Effective Date</Label>
        <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="w-48" data-testid="doc-effective-date" />
      </div>

      <div className="flex gap-2">
        <Button onClick={handlePreviewDoc} variant="outline" className="flex-1" data-testid="doc-preview-btn">
          <Eye className="h-4 w-4 mr-2" />Preview
        </Button>
        <Button onClick={handleDownloadDocx} disabled={generating} variant="outline" className="flex-1" data-testid="doc-download-btn">
          <FileDown className="h-4 w-4 mr-2" />{generating ? 'Generating...' : 'Download .docx'}
        </Button>
      </div>

      <div className="border-t pt-4 space-y-3">
        <h4 className="text-sm font-semibold">Send for Signature (Zoho Sign)</h4>
        <div className="grid grid-cols-1 gap-2">
          <Input type="email" placeholder="Employee Email" value={signerEmployee} onChange={e => setSignerEmployee(e.target.value)} data-testid="doc-signer-emp" />
          <Input type="email" placeholder="HR Email" value={signerHR} onChange={e => setSignerHR(e.target.value)} data-testid="doc-signer-hr" />
          <Input type="email" placeholder="Management Email" value={signerMgmt} onChange={e => setSignerMgmt(e.target.value)} data-testid="doc-signer-mgmt" />
        </div>
        <Button onClick={handleSendForSign} disabled={sendingSign} className="w-full" data-testid="doc-send-sign-btn">
          <PenTool className="h-4 w-4 mr-2" />{sendingSign ? 'Sending...' : 'Send for Signature'}
        </Button>
      </div>

      {signRequests.length > 0 && (
        <div className="border-t pt-3">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Signature History</h4>
          <div className="space-y-1.5">
            {signRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between text-xs p-2 rounded bg-muted/50">
                <span className="text-muted-foreground">{req.created_at ? new Date(req.created_at).toLocaleDateString() : '-'}</span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${req.status === 'sent' ? 'bg-blue-100 text-blue-700' : req.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {req.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmployeesPage() {
  const { canViewSalary, canEditSalary, canDeleteEmployees, canManageIncrements, isAdminLevel } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterVertical, setFilterVertical] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [noticeDialogOpen, setNoticeDialogOpen] = useState(false);
  const [noticeEmployee, setNoticeEmployee] = useState(null);
  const [noticeData, setNoticeData] = useState({ notice_start_date: '', last_working_date: '', notice_reason: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    employee_id: '',
    organization_id: '',
    vertical_id: '',
    gross_salary: '',
    retention_amount: '0',
    annual_bonus: '0',
    status: 'active',
    email: '',
    joining_date: '',
    department_id: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_name: '',
    account_holder_name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formVerticals, setFormVerticals] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Increment dialog state
  const [incrementDialogOpen, setIncrementDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [incrementData, setIncrementData] = useState({
    new_gross_salary: '',
    new_retention_amount: '',
    new_annual_bonus: '',
    effective_date: '',
    next_cycle_date: '',
    remarks: '',
  });
  const [incrementHistory, setIncrementHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.organization_id) {
      loadFormVerticals(formData.organization_id);
    } else {
      setFormVerticals([]);
    }
  }, [formData.organization_id]);

  const loadData = async () => {
    try {
      const [empRes, orgRes, vertRes, statusRes, deptRes] = await Promise.all([
        employeeAPI.getAll(),
        organizationAPI.getAll(),
        verticalAPI.getAll(),
        settingsAPI.getStatuses(),
        departmentAPI.getAll(),
      ]);
      setEmployees(empRes.data);
      setOrganizations(orgRes.data);
      setVerticals(vertRes.data);
      setStatuses(statusRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFormVerticals = async (orgId) => {
    try {
      const response = await verticalAPI.getAll(orgId);
      setFormVerticals(response.data);
    } catch (error) {
      console.error('Failed to load verticals');
    }
  };

  const handleOpenDialog = (emp = null) => {
    if (emp) {
      setEditingEmployee(emp);
      setFormData({
        name: emp.name,
        employee_id: emp.employee_id,
        organization_id: emp.organization_id,
        vertical_id: emp.vertical_id,
        gross_salary: (emp.gross_salary || emp.monthly_salary || 0).toString(),
        retention_amount: (emp.retention_amount || emp.retention_bonus || 0).toString(),
        annual_bonus: (emp.annual_bonus || 0).toString(),
        status: emp.status,
        email: emp.email || '',
        joining_date: emp.joining_date || '',
        department_id: emp.department_id || '',
        bank_account_number: emp.bank_account_number || '',
        bank_ifsc_code: emp.bank_ifsc_code || '',
        bank_name: emp.bank_name || '',
        account_holder_name: emp.account_holder_name || '',
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        name: '',
        employee_id: '',
        organization_id: '',
        vertical_id: '',
        gross_salary: '',
        retention_amount: '0',
        annual_bonus: '0',
        status: 'active',
        email: '',
        joining_date: '',
        department_id: '',
        bank_account_number: '',
        bank_ifsc_code: '',
        bank_name: '',
        account_holder_name: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        gross_salary: parseFloat(formData.gross_salary),
        retention_amount: parseFloat(formData.retention_amount) || 0,
        annual_bonus: parseFloat(formData.annual_bonus) || 0,
        department_id: formData.department_id || '',
      };

      // Remove salary fields if user cannot edit salary (e.g., LOP Manager)
      if (!canEditSalary()) {
        delete payload.gross_salary;
        delete payload.retention_amount;
        delete payload.annual_bonus;
        delete payload.salary_details;
        delete payload.monthly_salary;
      }
      
      if (editingEmployee) {
        await employeeAPI.update(editingEmployee.id, payload);
        toast.success('Employee updated successfully');
      } else {
        await employeeAPI.create(payload);
        toast.success('Employee created successfully');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (emp) => {
    try {
      await employeeAPI.delete(emp.id);
      toast.success('Employee deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleOpenNoticeDialog = (emp) => {
    setNoticeEmployee(emp);
    setNoticeData({
      notice_start_date: emp.notice_start_date || new Date().toISOString().split('T')[0],
      last_working_date: emp.last_working_date || '',
      notice_reason: emp.notice_reason || ''
    });
    setNoticeDialogOpen(true);
  };

  const handleMarkOnNotice = async () => {
    if (!noticeData.last_working_date) {
      toast.error('Last Working Date is required');
      return;
    }
    if (noticeData.notice_start_date && noticeData.last_working_date && noticeData.notice_start_date > noticeData.last_working_date) {
      toast.error('Notice Start Date cannot be after Last Working Date');
      return;
    }
    try {
      await employeeAPI.markOnNotice(noticeEmployee.id, noticeData);
      toast.success(`${noticeEmployee.name} notice updated`);
      setNoticeDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update notice');
    }
  };

  const handleCancelNotice = async (emp) => {
    try {
      await employeeAPI.cancelNotice(emp.id);
      toast.success(`Notice cancelled for ${emp.name}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel notice');
    }
  };

  const handleRehire = async (emp) => {
    try {
      await employeeAPI.rehire(emp.id);
      toast.success(`${emp.name} re-hired successfully`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-hire');
    }
  };

  const handleOpenIncrementDialog = async (emp) => {
    setSelectedEmployee(emp);
    // Pre-fill current values
    setIncrementData({
      new_gross_salary: emp.gross_salary || emp.monthly_salary || '',
      new_retention_amount: emp.retention_amount || emp.retention_bonus || '',
      new_annual_bonus: emp.annual_bonus || '',
      effective_date: new Date().toISOString().split('T')[0], // Today's date
      next_cycle_date: emp.next_increment_date || '',
      remarks: '',
    });
    setIncrementDialogOpen(true);
    setLoadingHistory(true);
    
    try {
      const response = await incrementAPI.getHistory(emp.id);
      setIncrementHistory(response.data.increments || []);
    } catch (error) {
      console.error('Failed to load increment history');
      setIncrementHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCreateIncrement = async () => {
    if (!selectedEmployee) return;
    
    const newSalary = parseFloat(incrementData.new_gross_salary);
    
    if (!newSalary || newSalary <= 0) {
      toast.error('Please enter valid new salary');
      return;
    }
    
    if (!incrementData.effective_date) {
      toast.error('Please enter effective date');
      return;
    }
    
    try {
      await incrementAPI.create({
        employee_id: selectedEmployee.id,
        new_gross_salary: newSalary,
        new_retention_amount: incrementData.new_retention_amount ? parseFloat(incrementData.new_retention_amount) : null,
        new_annual_bonus: incrementData.new_annual_bonus ? parseFloat(incrementData.new_annual_bonus) : null,
        effective_date: incrementData.effective_date,
        next_cycle_date: incrementData.next_cycle_date || null,
        remarks: incrementData.remarks || null,
      });
      toast.success('Increment applied successfully');
      setIncrementDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply increment');
    }
  };

  const getOrgName = (orgId) => organizations.find(o => o.id === orgId)?.name || '-';
  const getVerticalName = (vertId) => verticals.find(v => v.id === vertId)?.name || '-';
  const getDepartmentName = (deptId) => departments.find(d => d.id === deptId)?.name || '-';

  const handleEmailSaved = (empId, email) => {
    setEmployees(prev => prev.map(e => (e.id === empId ? { ...e, email } : e)));
  };

  const handleCopyAllEmails = async () => {
    const emails = filteredEmployees.map(e => (e.email || '').trim()).filter(Boolean);
    const skipped = filteredEmployees.length - emails.length;
    if (emails.length === 0) {
      toast.error('No emails found in the current list');
      return;
    }
    const ok = await copyText(emails.join(', '));
    if (ok) toast.success(`${emails.length} email${emails.length > 1 ? 's' : ''} copied${skipped ? ` · ${skipped} without email skipped` : ''}`);
    else toast.error('Could not copy to clipboard');
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employee_id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesOrg = filterOrg === 'all' || emp.organization_id === filterOrg;
    const matchesVertical = filterVertical === 'all' || emp.vertical_id === filterVertical;
    const matchesStatus = filterStatus === 'all' || emp.status === filterStatus;
    return matchesSearch && matchesOrg && matchesVertical && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const colors = {
      active: 'status-paid',
      inactive: 'status-pending',
      on_leave: 'status-finalized',
      on_notice: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-700',
      exited: 'bg-gray-200 text-gray-700 border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600',
      terminated: 'status-lop',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${colors[status] || 'status-pending'}`}>
        {status === 'on_notice' ? 'ON NOTICE' : status === 'exited' ? 'EXITED' : status.replace('_', ' ')}
      </span>
    );
  };

  const handleExportCSV = () => {
    const headers = ['Employee ID', 'Name', 'Organization', 'Vertical', 'Email', 'Status', 'Joining Date', 'Gross Salary', 'Retention', 'Net Eligible', 'Annual Bonus', 'Bank Name', 'Account Number', 'IFSC Code'];
    const rows = filteredEmployees.map(emp => {
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      const retention = emp.retention_amount || emp.retention_bonus || 0;
      return [
        emp.employee_id,
        emp.name,
        getOrgName(emp.organization_id),
        getVerticalName(emp.vertical_id),
        emp.email || '',
        emp.status,
        emp.joining_date || '',
        canViewSalary() ? gross : '',
        canViewSalary() ? retention : '',
        canViewSalary() ? (gross - retention) : '',
        canViewSalary() ? (emp.annual_bonus || 0) : '',
        emp.bank_name || '',
        emp.bank_account_number || '',
        emp.bank_ifsc_code || '',
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const handleExportPDF = () => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Build table rows HTML
    let tableRowsHTML = filteredEmployees.map(emp => {
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      const retention = emp.retention_amount || emp.retention_bonus || 0;
      const statusColors = {
        active: 'color: #059669',
        inactive: 'color: #d97706',
        on_leave: 'color: #7c3aed',
        terminated: 'color: #dc2626',
      };
      return `
        <tr>
          <td>${emp.employee_id}</td>
          <td>${emp.name}</td>
          <td>${getOrgName(emp.organization_id)}</td>
          <td>${getVerticalName(emp.vertical_id)}</td>
          <td>${emp.email || '-'}</td>
          <td><span style="${statusColors[emp.status] || ''}">${emp.status.replace('_', ' ')}</span></td>
          ${canViewSalary() ? `
            <td class="right">${formatCurrency(gross)}</td>
            <td class="right">${formatCurrency(retention)}</td>
            <td class="right">${formatCurrency(gross - retention)}</td>
          ` : ''}
        </tr>
      `;
    }).join('');

    const filterInfo = [];
    if (filterOrg !== 'all') filterInfo.push(`Organization: ${getOrgName(filterOrg)}`);
    if (filterVertical !== 'all') filterInfo.push(`Vertical: ${getVerticalName(filterVertical)}`);
    if (filterStatus !== 'all') filterInfo.push(`Status: ${filterStatus}`);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Employees Report - PayTrack</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 40px; color: #333; }
          .header { border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; color: #111; }
          .header p { color: #666; font-size: 14px; margin-top: 4px; }
          .filters { background: #f9fafb; padding: 12px; border-radius: 6px; margin-bottom: 20px; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { padding: 8px 6px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; color: #374151; }
          .right { text-align: right; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #999; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Employees Report</h1>
          <p>Generated on ${dateStr} • ${filteredEmployees.length} employees</p>
        </div>
        ${filterInfo.length > 0 ? `<div class="filters">Filters: ${filterInfo.join(' | ')}</div>` : ''}
        <table>
          <thead>
            <tr>
              <th>Employee ID</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Vertical</th>
              <th>Email</th>
              <th>Status</th>
              ${canViewSalary() ? `
                <th class="right">Gross Salary</th>
                <th class="right">Retention</th>
                <th class="right">Net Eligible</th>
              ` : ''}
            </tr>
          </thead>
          <tbody>
            ${tableRowsHTML}
          </tbody>
        </table>
        <div class="footer">PayTrack - Salary Reporting System</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF export ready for printing');
  };

  // Calculate net eligible salary
  const netEligible = parseFloat(formData.gross_salary || 0) - parseFloat(formData.retention_amount || 0);

  return (
    <DashboardLayout>
      <div data-testid="employees-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Employees</h1>
            <p className="text-muted-foreground mt-1">
              Manage employee records, salaries, and bank details
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} data-testid="add-employee-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingEmployee ? 'Edit Employee' : 'Add Employee'}
                </DialogTitle>
                <DialogDescription>
                  {editingEmployee ? 'Update employee details' : 'Create a new employee record'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className={`grid w-full ${canViewSalary() ? 'grid-cols-4' : 'grid-cols-2'}`}>
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    {canViewSalary() && <TabsTrigger value="salary">Salary & Bonus</TabsTrigger>}
                    <TabsTrigger value="bank">Bank Details</TabsTrigger>
                    {canViewSalary() && <TabsTrigger value="doc">Doc</TabsTrigger>}
                  </TabsList>
                  
                  <TabsContent value="basic" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="emp-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="employee_id">Employee ID</Label>
                        <Input
                          id="employee_id"
                          placeholder="EMP001"
                          value={formData.employee_id}
                          onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                          required
                          data-testid="emp-id-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Organization</Label>
                      <Select
                        value={formData.organization_id}
                        onValueChange={(value) => setFormData({ ...formData, organization_id: value, vertical_id: '' })}
                      >
                        <SelectTrigger data-testid="emp-org-select">
                          <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Vertical</Label>
                      <Select
                        value={formData.vertical_id}
                        onValueChange={(value) => setFormData({ ...formData, vertical_id: value })}
                        disabled={!formData.organization_id}
                      >
                        <SelectTrigger data-testid="emp-vertical-select">
                          <SelectValue placeholder={formData.organization_id ? "Select vertical" : "Select organization first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {formVerticals.map((vert) => (
                            <SelectItem key={vert.id} value={vert.id}>
                              {vert.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Select
                        value={formData.department_id}
                        onValueChange={(value) => setFormData({ ...formData, department_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger data-testid="emp-department-select">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— No department —</SelectItem>
                          {departments.map((dept) => (
                            <SelectItem key={dept.id} value={dept.id}>
                              {dept.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger data-testid="emp-status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status.replace('_', ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="joining_date">Joining Date</Label>
                        <Input
                          id="joining_date"
                          type="date"
                          value={formData.joining_date}
                          onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                          data-testid="emp-joining-date-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email (Optional)</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="emp-email-input"
                      />
                    </div>
                  </TabsContent>

                  {canViewSalary() && (
                    <TabsContent value="salary" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="gross_salary">Gross Salary (Monthly)</Label>
                          <Input
                            id="gross_salary"
                            type="number"
                            placeholder="30000"
                            value={formData.gross_salary}
                            onChange={(e) => setFormData({ ...formData, gross_salary: e.target.value })}
                            required
                            min="0"
                            step="0.01"
                            data-testid="emp-gross-salary-input"
                            disabled={!canEditSalary()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="retention_amount">Retention Amount (Monthly)</Label>
                          <Input
                            id="retention_amount"
                            type="number"
                            placeholder="5000"
                            value={formData.retention_amount}
                            onChange={(e) => setFormData({ ...formData, retention_amount: e.target.value })}
                            min="0"
                            step="0.01"
                            data-testid="emp-retention-input"
                            disabled={!canEditSalary()}
                          />
                        </div>
                      </div>

                      {/* Auto-calculated Net Eligible Salary */}
                      <div className="p-4 bg-muted/50 rounded-lg border">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Net Eligible Salary (Auto-calculated)</span>
                          <span className="text-lg font-bold font-mono text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(netEligible > 0 ? netEligible : 0)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Gross Salary - Retention Amount = Net Eligible Salary
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="annual_bonus">Annual Bonus <span className="text-[10px] text-muted-foreground font-normal">(yearly amount, paid once)</span></Label>
                        <Input
                          id="annual_bonus"
                          type="number"
                          placeholder="50000"
                          value={formData.annual_bonus}
                          onChange={(e) => setFormData({ ...formData, annual_bonus: e.target.value })}
                          min="0"
                          step="0.01"
                          data-testid="emp-annual-bonus-input"
                          disabled={!canEditSalary()}
                        />
                        <p className="text-[10px] text-muted-foreground">Yearly lump-sum. Not added to monthly Sub Total / PF / CTC. Shown only in Yearly column.</p>
                      </div>

                      {formData.retention_amount && parseFloat(formData.retention_amount) > 0 && (
                        <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <p className="text-sm text-amber-800 dark:text-amber-200">
                            <strong>Retention Policy:</strong> ₹{parseFloat(formData.retention_amount).toLocaleString('en-IN')} will be deducted monthly and released after 6 months (twice per FY).
                          </p>
                        </div>
                      )}

                      {/* Salary Structure Preview */}
                      {formData.gross_salary && parseFloat(formData.gross_salary) > 0 && (
                        <SalaryStructurePreview
                          gross={parseFloat(formData.gross_salary) || 0}
                          retention={parseFloat(formData.retention_amount) || 0}
                          annualBonus={parseFloat(formData.annual_bonus) || 0}
                          epfEnabled={organizations.find(o => o.id === formData.organization_id)?.epf_enabled || false}
                        />
                      )}
                    </TabsContent>
                  )}

                  <TabsContent value="bank" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="account_holder_name">Account Holder Name</Label>
                      <Input
                        id="account_holder_name"
                        placeholder="John Doe"
                        value={formData.account_holder_name}
                        onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                        data-testid="emp-account-holder-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bank_account_number">Bank Account Number</Label>
                      <Input
                        id="bank_account_number"
                        placeholder="1234567890"
                        value={formData.bank_account_number}
                        onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                        data-testid="emp-bank-account-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bank_ifsc_code">IFSC Code</Label>
                        <Input
                          id="bank_ifsc_code"
                          placeholder="SBIN0001234"
                          value={formData.bank_ifsc_code}
                          onChange={(e) => setFormData({ ...formData, bank_ifsc_code: e.target.value.toUpperCase() })}
                          data-testid="emp-ifsc-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank_name">Bank Name</Label>
                        <Input
                          id="bank_name"
                          placeholder="State Bank of India"
                          value={formData.bank_name}
                          onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                          data-testid="emp-bank-name-input"
                        />
                      </div>
                    </div>
                    <CancelledChequeUpload employeeId={editingEmployee?.id} />
                  </TabsContent>

                  {canViewSalary() && (
                    <TabsContent value="doc" className="space-y-4 mt-4">
                      <DocTabContent
                        employee={editingEmployee}
                        formData={formData}
                        organizations={organizations}
                      />
                    </TabsContent>
                  )}
                </Tabs>

                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="emp-submit-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 spinner" />
                        Saving...
                      </>
                    ) : editingEmployee ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="emp-search-input"
            />
          </div>
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-[180px]" data-testid="filter-org-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterVertical} onValueChange={setFilterVertical}>
            <SelectTrigger className="w-[150px]" data-testid="filter-vertical-select">
              <SelectValue placeholder="Vertical" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Verticals</SelectItem>
              {verticals.map((vert) => (
                <SelectItem key={vert.id} value={vert.id}>{vert.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>{status.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleCopyAllEmails} data-testid="copy-all-emails-btn">
            <Mail className="h-4 w-4 mr-2" />
            Copy All Emails
          </Button>
          <Button variant="outline" onClick={() => setBulkEmailOpen(true)} data-testid="bulk-email-import-open-btn">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import Emails
          </Button>
        </div>

        <BulkEmailImportDialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen} onImported={loadData} />

        {/* Count summary */}
        <EmployeeCountCard
          filtered={filteredEmployees}
          total={employees.length}
          filterLabels={[
            filterOrg !== 'all' ? (organizations.find((o) => o.id === filterOrg) || {}).name : null,
            filterVertical !== 'all' ? (verticals.find((v) => v.id === filterVertical) || {}).name : null,
            filterStatus !== 'all' ? filterStatus.replace('_', ' ') : null,
            searchQuery ? `"${searchQuery}"` : null,
          ].filter(Boolean)}
        />

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery || filterOrg !== 'all' || filterVertical !== 'all' || filterStatus !== 'all'
                    ? 'No employees found matching filters'
                    : 'No employees yet'}
                </p>
                {!searchQuery && filterOrg === 'all' && (
                  <Button variant="link" onClick={() => handleOpenDialog()} className="mt-2">
                    Add your first employee
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Vertical</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.map((emp, empIdx) => {
                      const gross = emp.gross_salary || emp.monthly_salary || 0;
                      const retention = emp.retention_amount || emp.retention_bonus || 0;
                      const netElig = gross - retention;
                      
                      return (
                        <TableRow key={emp.id} className={`table-row-hover ${emp.status === 'on_notice' ? 'bg-orange-50 dark:bg-orange-950/30' : ''}`} data-testid={`emp-row-${emp.id}`}>
                          <TableCell className="text-sm text-muted-foreground" data-testid={`emp-serial-${emp.id}`}>{empIdx + 1}</TableCell>
                          <TableCell className="font-mono text-sm">{emp.employee_id}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{emp.name}</span>
                              {emp.status === 'on_notice' && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500 text-white" data-testid={`notice-badge-${emp.id}`}>
                                  <AlertTriangle className="h-3 w-3" />
                                  ON NOTICE
                                </span>
                              )}
                            </div>
                            {emp.status === 'on_notice' && emp.last_working_date && (
                              <p className="text-[11px] text-orange-600 dark:text-orange-400 mt-0.5">LWD: {emp.last_working_date}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1.5">
                              {getOrgName(emp.organization_id)}
                              <EpfBadge enabled={organizations.find(o => o.id === emp.organization_id)?.epf_enabled} />
                            </span>
                          </TableCell>
                          <TableCell>{getVerticalName(emp.vertical_id)}</TableCell>
                          <TableCell data-testid={`emp-dept-${emp.id}`}>{getDepartmentName(emp.department_id)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{emp.joining_date || '-'}</TableCell>
                          <TableCell><EmailInlineCell employee={emp} onSaved={handleEmailSaved} /></TableCell>
                          <TableCell>{getStatusBadge(emp.status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canManageIncrements() && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenIncrementDialog(emp)}
                                  title="Increment"
                                  data-testid={`increment-emp-${emp.id}`}
                                >
                                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(emp)}
                                data-testid={`edit-emp-${emp.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              {canDeleteEmployees() && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-destructive hover:text-destructive"
                                      data-testid={`delete-emp-${emp.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Employee</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete "{emp.name}"? This will also delete all associated salary records. This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(emp)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                              )}
                              {/* Notice Period Actions */}
                              {isAdminLevel() && emp.status === 'active' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenNoticeDialog(emp)}
                                  title="Mark on Notice"
                                  data-testid={`notice-emp-${emp.id}`}
                                >
                                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                                </Button>
                              )}
                              {isAdminLevel() && emp.status === 'on_notice' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenNoticeDialog(emp)}
                                  title="Edit Notice Dates"
                                  data-testid={`edit-notice-${emp.id}`}
                                >
                                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                                </Button>
                              )}
                              {isAdminLevel() && emp.status === 'on_notice' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCancelNotice(emp)}
                                  title="Cancel Notice"
                                  data-testid={`cancel-notice-${emp.id}`}
                                >
                                  <XCircle className="h-4 w-4 text-orange-600" />
                                </Button>
                              )}
                              {isAdminLevel() && emp.status === 'exited' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" title="Re-hire Employee" data-testid={`rehire-${emp.id}`}>
                                      <UserCheck className="h-4 w-4 text-emerald-600" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Re-hire {emp.name}?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will set <strong>{emp.name}</strong> ({emp.employee_id}) back to active status. They will appear in future salary generation.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleRehire(emp)} className="bg-emerald-600 hover:bg-emerald-700">
                                        Re-hire
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Increment Dialog */}
        <Dialog open={incrementDialogOpen} onOpenChange={setIncrementDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                Salary Increment
              </DialogTitle>
              <DialogDescription>
                {selectedEmployee && `Apply increment for ${selectedEmployee.name} (${selectedEmployee.employee_id})`}
              </DialogDescription>
            </DialogHeader>
            
            {selectedEmployee && (
              <div className="space-y-6">
                {/* Current Values Summary */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Current Gross Salary</p>
                    <p className="text-lg font-bold font-mono">
                      {formatCurrency(selectedEmployee.gross_salary || selectedEmployee.monthly_salary || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Current Retention</p>
                    <p className="text-lg font-bold font-mono">
                      {formatCurrency(selectedEmployee.retention_amount || selectedEmployee.retention_bonus || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Current Annual Bonus</p>
                    <p className="text-lg font-bold font-mono">
                      {formatCurrency(selectedEmployee.annual_bonus || 0)}
                    </p>
                  </div>
                </div>

                {/* Salary & Bonus Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium border-b pb-2">New Salary & Bonus Details</h4>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new_salary">Gross Salary <span className="text-destructive">*</span></Label>
                      <Input
                        id="new_salary"
                        type="number"
                        placeholder="Enter new salary"
                        value={incrementData.new_gross_salary}
                        onChange={(e) => setIncrementData({ ...incrementData, new_gross_salary: e.target.value })}
                        data-testid="increment-new-salary-input"
                      />
                      {incrementData.new_gross_salary && (
                        <p className={`text-xs ${parseFloat(incrementData.new_gross_salary) > (selectedEmployee.gross_salary || 0) ? 'text-emerald-600' : 'text-destructive'}`}>
                          Change: {formatCurrency(parseFloat(incrementData.new_gross_salary) - (selectedEmployee.gross_salary || selectedEmployee.monthly_salary || 0))}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new_retention">Retention Amount</Label>
                      <Input
                        id="new_retention"
                        type="number"
                        placeholder="Enter retention"
                        value={incrementData.new_retention_amount}
                        onChange={(e) => setIncrementData({ ...incrementData, new_retention_amount: e.target.value })}
                        data-testid="increment-new-retention-input"
                      />
                      {incrementData.new_retention_amount && parseFloat(incrementData.new_retention_amount) !== (selectedEmployee.retention_amount || 0) && (
                        <p className="text-xs text-muted-foreground">
                          Change: {formatCurrency(parseFloat(incrementData.new_retention_amount) - (selectedEmployee.retention_amount || 0))}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new_bonus">Annual Bonus</Label>
                      <Input
                        id="new_bonus"
                        type="number"
                        placeholder="Enter bonus"
                        value={incrementData.new_annual_bonus}
                        onChange={(e) => setIncrementData({ ...incrementData, new_annual_bonus: e.target.value })}
                        data-testid="increment-new-bonus-input"
                      />
                      {incrementData.new_annual_bonus && parseFloat(incrementData.new_annual_bonus) !== (selectedEmployee.annual_bonus || 0) && (
                        <p className="text-xs text-muted-foreground">
                          Change: {formatCurrency(parseFloat(incrementData.new_annual_bonus) - (selectedEmployee.annual_bonus || 0))}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dates Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium border-b pb-2">Dates & Remarks</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="effective_date">Effective Date <span className="text-destructive">*</span></Label>
                      <Input
                        id="effective_date"
                        type="date"
                        value={incrementData.effective_date}
                        onChange={(e) => setIncrementData({ ...incrementData, effective_date: e.target.value })}
                        data-testid="increment-date-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="next_cycle_date">Next Cycle Date</Label>
                      <Input
                        id="next_cycle_date"
                        type="date"
                        value={incrementData.next_cycle_date}
                        onChange={(e) => setIncrementData({ ...incrementData, next_cycle_date: e.target.value })}
                        data-testid="increment-next-cycle-input"
                      />
                      <p className="text-xs text-muted-foreground">When is the next increment due?</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="remarks">Remarks</Label>
                    <Input
                      id="remarks"
                      placeholder="Annual appraisal, promotion, performance bonus, etc."
                      value={incrementData.remarks}
                      onChange={(e) => setIncrementData({ ...incrementData, remarks: e.target.value })}
                    />
                  </div>
                </div>

                {/* Increment History */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2 border-b pb-2">
                    <History className="h-4 w-4" />
                    Increment History
                  </h4>
                  {loadingHistory ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 spinner" />
                    </div>
                  ) : incrementHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No previous increments</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {incrementHistory.map((inc, index) => (
                        <div key={inc.id || index} className="p-3 bg-muted/30 rounded-lg text-sm border">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-medium">Gross: </span>
                              <span className="font-mono">{formatCurrency(inc.previous_gross_salary)} → {formatCurrency(inc.new_gross_salary)}</span>
                              <span className="text-emerald-600 ml-2">(+{formatCurrency(inc.increment_amount || (inc.new_gross_salary - inc.previous_gross_salary))})</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{inc.effective_date}</span>
                          </div>
                          {(inc.previous_retention_amount !== undefined || inc.new_retention_amount !== undefined) && 
                           inc.previous_retention_amount !== inc.new_retention_amount && (
                            <div className="text-xs text-muted-foreground">
                              <span>Retention: </span>
                              <span className="font-mono">{formatCurrency(inc.previous_retention_amount || 0)} → {formatCurrency(inc.new_retention_amount || 0)}</span>
                            </div>
                          )}
                          {(inc.previous_annual_bonus !== undefined || inc.new_annual_bonus !== undefined) && 
                           inc.previous_annual_bonus !== inc.new_annual_bonus && (
                            <div className="text-xs text-muted-foreground">
                              <span>Annual Bonus: </span>
                              <span className="font-mono">{formatCurrency(inc.previous_annual_bonus || 0)} → {formatCurrency(inc.new_annual_bonus || 0)}</span>
                            </div>
                          )}
                          {inc.next_cycle_date && (
                            <div className="text-xs text-muted-foreground">
                              <span>Next Cycle: </span>
                              <span>{inc.next_cycle_date}</span>
                            </div>
                          )}
                          {inc.remarks && (
                            <p className="text-xs text-muted-foreground mt-1 italic">"{inc.remarks}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIncrementDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateIncrement}
                disabled={!incrementData.new_gross_salary || !incrementData.effective_date}
                data-testid="apply-increment-btn"
              >
                Apply Increment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notice Period Dialog */}
        <Dialog open={noticeDialogOpen} onOpenChange={setNoticeDialogOpen}>
          <DialogContent className="sm:max-w-[425px]" data-testid="notice-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="h-5 w-5" />
                Mark on Notice
              </DialogTitle>
              <DialogDescription>
                {noticeEmployee?.status === 'on_notice' ? 'Update' : 'Mark'} <strong>{noticeEmployee?.name}</strong> ({noticeEmployee?.employee_id}) {noticeEmployee?.status === 'on_notice' ? 'notice details.' : 'as on notice period.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm font-medium">Notice Start Date *</Label>
                <Input
                  type="date"
                  value={noticeData.notice_start_date}
                  onChange={(e) => setNoticeData(d => ({ ...d, notice_start_date: e.target.value }))}
                  data-testid="notice-start-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Last Working Date (LWD) *</Label>
                <Input
                  type="date"
                  value={noticeData.last_working_date}
                  onChange={(e) => setNoticeData(d => ({ ...d, last_working_date: e.target.value }))}
                  data-testid="notice-lwd"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Reason</Label>
                <Select value={noticeData.notice_reason} onValueChange={(v) => setNoticeData(d => ({ ...d, notice_reason: v }))}>
                  <SelectTrigger data-testid="notice-reason">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resignation">Resignation</SelectItem>
                    <SelectItem value="termination">Termination</SelectItem>
                    <SelectItem value="mutual_agreement">Mutual Agreement</SelectItem>
                    <SelectItem value="contract_end">Contract End</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoticeDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleMarkOnNotice}
                disabled={!noticeData.last_working_date || !noticeData.notice_start_date}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="confirm-notice-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {noticeEmployee?.status === 'on_notice' ? 'Update Notice' : 'Mark on Notice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
